import { FC, useMemo } from 'react';
import { FLOWS, MALLO_LEVELS, percentuale } from '@leopart/calc';
import DistributionBars from '../../../shared/charts/DistributionBars';
import { DistributionSlice } from '../../../shared/charts/chart.types';
import Typhography from '../../../shared/Typhography/Typhography';
import KpiCard from '../../../shared/KpiCard/KpiCard';
import { notaRapportoCI } from '../allenamenti.logica';
import { ITrainingMixMalloIdc } from '../../../models/allenamenti';
import { formatMinutes } from './analysis.constants';
import styles from './MalloIdcPanel.module.scss';

/** "62% · 12m" (o solo "62%"/"—") — usato per Cognitivo/Muscolare.
 * Guard difensivo sui minuti: con payload parziale (campo assente) mostra solo
 * la percentuale invece di "NaNm". */
function formatPctWithMinutes(
  pct: number | null,
  minutes: number | null | undefined,
): string {
  if (pct == null) return '—';
  const base = `${Math.round(pct)}%`;
  return typeof minutes === 'number' && Number.isFinite(minutes)
    ? `${base} · ${formatMinutes(minutes)}`
    : base;
}

interface IMalloIdcPanelProps {
  /** `null` = nessun blocco con palla nel periodo. */
  mallo: ITrainingMixMalloIdc | null;
}

/**
 * Pannello «Lavoro tecnico-tattico — Scala Mallo & IDC» (ADR 029): CE-tecnico,
 * IDC, quote cognitiva/muscolare, rapporto Continuo/Intermittente con alert
 * sotto 1.0, distribuzioni per livello e per flusso, esclusi conteggiati, e
 * spiegazione della scala in un espandibile chiuso di default.
 */
const MalloIdcPanel: FC<IMalloIdcPanelProps> = ({ mallo }) => {
  const levelBars: DistributionSlice[] = useMemo(() => {
    if (!mallo) return [];
    const total = mallo.byLevel.reduce((s, l) => s + l.minutes, 0);
    return MALLO_LEVELS.map((level) => {
      const minutes =
        mallo.byLevel.find((l) => l.level === level.v)?.minutes ?? 0;
      return {
        id: String(level.v),
        label: level.labelIt,
        value: minutes,
        percent: percentuale(minutes, total),
        color: level.color,
      };
    });
  }, [mallo]);

  const flowBars: DistributionSlice[] = useMemo(() => {
    if (!mallo) return [];
    const total = mallo.byFlow.reduce((s, f) => s + f.minutes, 0);
    return FLOWS.map((flow) => {
      const minutes =
        mallo.byFlow.find((f) => f.flow === flow.id)?.minutes ?? 0;
      return {
        id: flow.id,
        label: `${flow.labelIt} (×${flow.fattore.toFixed(2)})`,
        value: minutes,
        percent: percentuale(minutes, total),
        color: flow.color,
      };
    });
  }, [mallo]);

  const ciNote = mallo ? notaRapportoCI(mallo) : '';

  return (
    <div className={styles.card}>
      <Typhography type="h3" size="bold-14" className={styles.title}>
        Lavoro tecnico-tattico — Scala Mallo &amp; IDC
      </Typhography>
      <Typhography size="regular-12" className={styles.subtitle}>
        CE-tecnico in % di gara equivalente; l&apos;IDC corregge il valore
        nominale per il fattore di flusso (Continuo ×1.00 · Intermittente ×0.65
        · Analitico ×0.45). Minuti ponderati sul tempo effettivo quando
        presente.
      </Typhography>

      {!mallo ? (
        <p className={styles.empty}>
          Nessun blocco tecnico-tattico nel periodo.
        </p>
      ) : mallo.ce == null ? (
        <p className={styles.empty}>
          Blocchi tecnico-tattici presenti ({mallo.eligibleBlocks}) ma senza
          peso Mallo assegnato: nessun calcolo possibile.
        </p>
      ) : (
        <>
          <div className={styles.kpiGrid}>
            <KpiCard label="CE-tecnico" value={String(mallo.ce)} sub="% di gara equivalente" />
            <KpiCard
              label="IDC"
              value={mallo.idc != null ? String(mallo.idc) : '—'}
              sub="CE corretto per flusso"
            />
            <KpiCard
              label="Cognitivo"
              value={formatPctWithMinutes(mallo.cognitivePct, mallo.cognitiveMinutes)}
              sub="minuti ≥60% (opposizione completa)"
            />
            <KpiCard
              label="Muscolare"
              value={formatPctWithMinutes(mallo.muscularPct, mallo.muscularMinutes)}
              sub="minuti ≥80% (SSG parità + partita)"
            />
            <KpiCard
              label="Continuo / Interm."
              value={mallo.ciRatio != null ? mallo.ciRatio.toFixed(2) : '—'}
              sub={ciNote}
              alert={mallo.ciAlert}
            />
          </div>

          <Typhography size="bold-12" className={styles.miniTitle}>
            Minuti per peso Mallo
          </Typhography>
          <DistributionBars data={levelBars} valueFormatter={formatMinutes} labelMode="value-percent" />

          <Typhography size="bold-12" className={styles.miniTitle}>
            Minuti per densità di flusso
          </Typhography>
          <DistributionBars data={flowBars} valueFormatter={formatMinutes} labelMode="value-percent" />

          {(mallo.missingMallo > 0 || mallo.missingFlow > 0) && (
            <p className={styles.meta}>
              {mallo.missingMallo > 0 && (
                <>
                  <b>{mallo.missingMallo}</b> blocchi senza peso Mallo
                </>
              )}
              {mallo.missingMallo > 0 && mallo.missingFlow > 0 && ' · '}
              {mallo.missingFlow > 0 && (
                <>
                  <b>{mallo.missingFlow}</b> senza flusso
                </>
              )}{' '}
              — restano validi, sono solo esclusi dai rispettivi calcoli (campi
              facoltativi).
            </p>
          )}
        </>
      )}

      <details className={styles.details}>
        <summary>Come leggere questi numeri (per chi non conosce Mallo)</summary>
        <p>
          Il carico di una seduta con palla non dipende solo dai minuti: 15&apos;
          di 5c5 e 15&apos; di tecnica analitica pesano in modo molto diverso su
          testa e muscoli. La scala di <b>Javier Mallo</b> (ex Real Madrid e
          Manchester City) assegna a ogni esercitazione un peso in % di gara:
          più avversari veri, spazio e decisioni, più il peso sale.
        </p>
        <p>
          <b>CE-tecnico</b>: quanto «vale» il lavoro con palla del periodo
          rispetto a una partita — 100 = solo partita libera, 30 = solo tecnica
          analitica.
          <br />
          <b>IDC</b> (Indice di Densità Continua): lo stesso valore corretto per
          come il carico viene erogato — il lavoro a flusso continuo vale pieno,
          quello a fasi ×0.65, l&apos;analitico ×0.45. Se l&apos;IDC è molto
          sotto il CE-tecnico, il periodo «sembra da partita» sulla carta ma è
          vissuto a tratti.
          <br />
          <b>Cognitivo</b>: quota di minuti con opposizione completa o vincoli
          di gioco (60-80-100%) — densità decisionale.
          <br />
          <b>Muscolare</b>: quota all&apos;80-100% (SSG in parità e partita),
          dove compaiono sprint, frenate e duelli con richieste su hamstring e
          quadricipiti simili alla gara.
          <br />
          <b>Continuo/Intermittente</b>: se resta a lungo sotto <b>1.0</b> in
          periodo di campionato, le sedute sono spostate sul lavoro a fasi
          mentre la gara è a flusso continuo — utile inserire almeno una SSG a
          flusso continuo (5c5/6c6 in spazio ridotto) nel microciclo.
        </p>
      </details>
    </div>
  );
};

export default MalloIdcPanel;
